/**
 * AttachPlanModal (Phase 2.7)
 *
 * Opens from VisitProceduresPanel via the new "📋 Attach plan" button.
 * Lets the caller materialise a PatientPlan from a workflow master:
 *
 *   1. Pick a workflow plan from a dropdown (caller's own active plans;
 *      admin sees all).
 *   2. Show the step list. Mandatory steps are locked-checked. Optional
 *      steps are checked-by-default but can be un-checked to opt out.
 *   3. Per-step assignee picker (TeamAssignmentPicker) defaulting to the
 *      master's default_executor_member_id; resolves any missing slots.
 *      Same for approver when requires_approval is set.
 *   4. Submit → calls treatmentPlanMasterStore.attachToVisit.
 *      On MISSING_ASSIGNEES error, the offending step rows highlight in
 *      red and a "Default all to me" shortcut pre-fills the doctor's id.
 *
 * Day-mode contrast + button-visibility rules from CLAUDE.md applied.
 */
import React, { useEffect, useMemo, useState } from 'react';

import useDoctorRateStore from '../../stores/doctorRateStore';
import useDoctorTeamStore from '../../stores/doctorTeamStore';
import useProcedureStore from '../../stores/procedureStore';
import useTreatmentPlanMasterStore from '../../stores/treatmentPlanMasterStore';
import useDepartmentStore from '../../stores/departmentStore';
import { fetchStaffList } from '../../services/api/staffApi';
import { formatStaffName } from '../../utils/staffName';
import TeamAssignmentPicker from './TeamAssignmentPicker';
// A2-S6 — shared department/role guard error mapping (single source of truth,
// same helper QuickSearchAdd uses).
import { addProcedureErrorMessage } from './assigneeGuardError';

// Phase 2.8 — per-step date radio modes.
const DATE_MODE_TODAY = 'today';
const DATE_MODE_PICK = 'pick';
const DATE_MODE_PLAN_ONLY = 'plan_only';

const todayIso = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};


export default function AttachPlanModal({
  open,
  onClose,
  visit,
  viewer,
  onAttached,
  // Phase 7 (Phase 6.0 plan) — auto-close UX fix.
  // When true, suppress the legacy `window.open` to a new PatientPlan
  // tab; the parent is expected to handle the post-attach UX via
  // `onAttached`. Default false preserves legacy behavior for any
  // caller that hasn't migrated yet.
  suppressPostAttachNavigation = false,
}) {
  const isAdmin = (viewer?.role || '').toUpperCase() === 'ADMIN';

  const plans = useTreatmentPlanMasterStore((s) => s.plans);
  const fetchPlans = useTreatmentPlanMasterStore((s) => s.fetchPlans);
  const isLoadingPlans = useTreatmentPlanMasterStore((s) => s.isLoading);
  const attachToVisit = useTreatmentPlanMasterStore((s) => s.attachToVisit);
  const planById = useTreatmentPlanMasterStore((s) => s.getPlanById);

  const procedures = useProcedureStore((s) => s.procedures);
  const fetchProcedures = useProcedureStore((s) => s.fetchProcedures);

  const fetchTeams = useDoctorTeamStore((s) => s.fetchTeams);
  const teamsLoaded = useDoctorTeamStore((s) => s.teamsLoaded);

  // A2 completion — dept-scope the per-step executor/approver pickers to the
  // VISIT'S stamped department of care. Shared member→dept map + one-time bulk
  // hydrate; anchor = visit.department_id (falsy → no filter / degrade).
  const deptsByMember = useDepartmentStore((s) => s.deptsByMember);
  const hydrateAllMemberDepts = useDepartmentStore((s) => s.hydrateAllMemberDepts);
  const visitDepartmentId = visit?.department_id || null;

  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [paymentMode, setPaymentMode] = useState('per_step');  // 'per_step' | 'upfront'
  const [stepDraft, setStepDraft] = useState({});
  /* stepDraft shape:
       {
         [step_id]: {
           included: bool,
           executor_member_id: string,
           approver_member_id: string,
           date_mode: 'today' | 'pick' | 'plan_only',
           picked_date: 'YYYY-MM-DD' | '',
         }
       }
  */
  const [staff, setStaff] = useState([]);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [missingStepIds, setMissingStepIds] = useState([]);

  // Phase 2.8 — owner-doctor rate map for client-side estimate.
  const rates = useDoctorRateStore((s) => s.rates);
  const selectDoctor = useDoctorRateStore((s) => s.selectDoctor);
  const ratesDoctorId = useDoctorRateStore((s) => s.selectedDoctorId);

  useEffect(() => {
    if (!open) return;
    fetchPlans();
    fetchTeams();
    if (!procedures.length) fetchProcedures();
    fetchStaffList()
      .then((r) => {
        if (r?.success) {
          // /api/organisation/mem returns List[StaffMemberResponse]
          // directly under r.data; tolerate wrapped shapes too.
          const rows = Array.isArray(r.data)
            ? r.data
            : (r.data?.staff || r.data?.members || []);
          const list = rows.map((m) => ({
            member_id: m.member_id,
            role: (m.role || '').toUpperCase(),
            name: formatStaffName(m),
            first_name: m.first_name,
            last_name: m.last_name,
            specialization: m.specialization,
          }));
          setStaff(list);
        }
      })
      .catch(() => { /* non-fatal */ });
  }, [open, fetchPlans, fetchTeams, fetchProcedures, procedures.length]);

  useEffect(() => {
    if (open && visitDepartmentId) hydrateAllMemberDepts?.();
  }, [open, visitDepartmentId, hydrateAllMemberDepts]);

  const visiblePlans = useMemo(() => {
    if (isAdmin) return plans;
    return plans.filter((p) => p.owner_doctor_member_id === viewer?.memberId);
  }, [plans, isAdmin, viewer]);

  const selectedPlan = useMemo(
    () => (selectedPlanId ? planById(selectedPlanId) : null),
    [selectedPlanId, planById],
  );

  // Resolve the owner doctor's display name from the staff roster so the
  // TeamAssignmentPicker's "Me (Dr. ___)" label is meaningful when an
  // admin attaches another doctor's plan. Falls back to the raw id.
  const ownerDoctorDisplayName = useMemo(() => {
    if (!selectedPlan) return null;
    const ownerId = selectedPlan.owner_doctor_member_id;
    const hit = staff.find((s) => s.member_id === ownerId);
    return hit?.name || ownerId || null;
  }, [selectedPlan, staff]);

  // Hydrate stepDraft when the user picks a plan.
  useEffect(() => {
    if (!selectedPlan) {
      setStepDraft({});
      return;
    }
    const next = {};
    for (const s of selectedPlan.steps || []) {
      next[s.step_id] = {
        included: true,
        executor_member_id: s.default_executor_member_id || '',
        approver_member_id: s.default_approver_member_id || '',
        date_mode: DATE_MODE_PLAN_ONLY,
        picked_date: '',
      };
    }
    setStepDraft(next);
    setPaymentMode('per_step');
    setMissingStepIds([]);
    setErrorMsg(null);
  }, [selectedPlan?.plan_id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Pull the owner-doctor's rate-override map so the estimate footer
  // reflects override → catalog default priority.
  useEffect(() => {
    if (!selectedPlan) return;
    const ownerId = selectedPlan.owner_doctor_member_id;
    if (ownerId && ownerId !== ratesDoctorId) {
      selectDoctor(ownerId);
    }
  }, [selectedPlan, ratesDoctorId, selectDoctor]);

  const procedureNameById = useMemo(() => {
    const map = {};
    for (const p of procedures) map[p.procedure_id] = p.name;
    return map;
  }, [procedures]);

  // Phase 2.8 — running estimate. For each *included* step, prefer the
  // step's per-step `estimated_cost_override` → owner doctor's rate
  // override → procedure catalog default. Excluded steps don't count.
  const procedureCostById = useMemo(() => {
    const map = {};
    for (const p of procedures) {
      map[p.procedure_id] = Number(p.default_cost) || 0;
    }
    return map;
  }, [procedures]);

  const totalEstimate = useMemo(() => {
    if (!selectedPlan) return 0;
    let total = 0;
    for (const s of selectedPlan.steps || []) {
      const d = stepDraft[s.step_id] || { included: true };
      if (!d.included) continue;
      if (s.estimated_cost_override != null && s.estimated_cost_override !== '') {
        total += Number(s.estimated_cost_override) || 0;
        continue;
      }
      const override = rates?.[s.procedure_id]?.override_cost;
      if (override != null) {
        total += Number(override) || 0;
        continue;
      }
      total += procedureCostById[s.procedure_id] || 0;
    }
    const discount = Number(selectedPlan.package_discount) || 0;
    return Math.max(0, total - discount);
  }, [selectedPlan, stepDraft, rates, procedureCostById]);

  const setStep = (stepId, patch) => {
    setStepDraft((prev) => ({
      ...prev,
      [stepId]: { ...prev[stepId], ...patch },
    }));
  };

  const defaultAllToMe = () => {
    if (!selectedPlan) return;
    const next = { ...stepDraft };
    const me = viewer?.memberId;
    if (!me) return;
    for (const s of selectedPlan.steps || []) {
      const cur = next[s.step_id] || { included: true };
      if (!cur.executor_member_id) {
        next[s.step_id] = {
          ...cur,
          executor_member_id: me,
        };
      }
    }
    setStepDraft(next);
    setMissingStepIds([]);
  };

  const onSubmit = async () => {
    if (!selectedPlan || !visit) return;
    setBusy(true);
    setErrorMsg(null);
    setMissingStepIds([]);

    const excluded_step_ids = [];
    const assignee_resolutions = {};
    const step_dates = {};
    for (const s of selectedPlan.steps || []) {
      const d = stepDraft[s.step_id] || { included: true };
      if (!d.included) {
        if (s.is_mandatory) {
          setErrorMsg(
            `Step "${procedureNameById[s.procedure_id] || s.procedure_id}"`
            + ' is mandatory and cannot be excluded.',
          );
          setBusy(false);
          return;
        }
        excluded_step_ids.push(s.step_id);
        continue;
      }
      const r = {};
      if (d.executor_member_id) r.executor_member_id = d.executor_member_id;
      if (s.requires_approval && d.approver_member_id) {
        r.approver_member_id = d.approver_member_id;
      }
      if (Object.keys(r).length) assignee_resolutions[s.step_id] = r;

      // Phase 2.8 — three-way date radio → backend step_dates map.
      if (d.date_mode === DATE_MODE_TODAY) {
        step_dates[s.step_id] = todayIso();
      } else if (d.date_mode === DATE_MODE_PICK && d.picked_date) {
        step_dates[s.step_id] = d.picked_date;
      }
      // DATE_MODE_PLAN_ONLY → omit (plan-only step, no schedule yet).
    }

    try {
      const r = await attachToVisit(selectedPlan.plan_id, {
        patient_id: visit.patient_id,
        source_visit_id: visit.visit_id,
        payment_mode: paymentMode,
        step_dates: Object.keys(step_dates).length ? step_dates : undefined,
        excluded_step_ids: excluded_step_ids.length ? excluded_step_ids : undefined,
        assignee_resolutions: Object.keys(assignee_resolutions).length
          ? assignee_resolutions : undefined,
        ...(isAdmin && selectedPlan.owner_doctor_member_id !== viewer?.memberId
          ? { owner_doctor_member_id: selectedPlan.owner_doctor_member_id } : {}),
      });
      if (!r.success) {
        if (r.errorCode === 'MISSING_ASSIGNEES') {
          const ids = r.errorPayload?.step_ids || [];
          setMissingStepIds(ids);
          setErrorMsg(
            r.errorPayload?.message
            || 'Some steps need an assignee on this doctor\'s team. '
               + 'Pick a team member or click "Default all to me".',
          );
          return;
        }
        // A2-S6 — a bare 403 (out-of-department) / 400 (role-ineligible)
        // guard rejection with no specific server detail collapses to
        // buildApiError's generic string; map it to the same readable
        // eligibility hint the Smart Workflow add-flow uses. A specific
        // server `detail` still passes through verbatim; non-guard errors
        // (500 etc.) are untouched. (No client-side dept pre-filtering.)
        setErrorMsg(addProcedureErrorMessage(r, 'Attach failed.'));
        return;
      }
      // Phase 2.8 — legacy: open PatientPlanPage in a new tab so the
      // receptionist can collect payment + start steps without losing
      // the visit context.
      // Phase 6.0 / 7 — when `suppressPostAttachNavigation` is set, the
      // parent owns the post-attach UX (typically opens
      // ActionPendingDialog with to_role=RECEPTIONIST so the doctor
      // explicitly hands the visit over for payment instead of being
      // teleported to a new tab).
      if (!suppressPostAttachNavigation) {
        const newPlanId = r.data?.plan?.plan_id || r.data?.plan_id;
        if (newPlanId && typeof window !== 'undefined') {
          window.open(`/org/patient-plans/${newPlanId}`, '_blank', 'noopener');
        }
      }
      onAttached?.(r.data);
      onClose?.();
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="w-full max-w-3xl bg-white dark:bg-slate-900 rounded-xl shadow-xl overflow-hidden text-slate-900 dark:text-slate-100">
        <div className="flex items-center justify-between px-5 py-3 border-b-2 border-indigo-300 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/40">
          <div>
            <h2 className="text-base font-bold text-indigo-900 dark:text-indigo-100">
              📋 Attach plan
            </h2>
            <p className="text-xs font-medium text-indigo-800 dark:text-indigo-200">
              Materialise a workflow plan as a snapshot for this patient&apos;s visit.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-medium px-3 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            Close
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Plan picker */}
          <label className="block">
            <span className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
              Pick a treatment plan
            </span>
            <select
              value={selectedPlanId}
              onChange={(e) => setSelectedPlanId(e.target.value)}
              disabled={busy || isLoadingPlans}
              className="w-full text-sm font-medium px-2.5 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
            >
              <option value="">— Select a plan —</option>
              {visiblePlans.map((p) => (
                <option key={p.plan_id} value={p.plan_id}>
                  {p.plan_name}
                  {' · '}{(p.steps || []).length} steps
                  {' · '}{p.ordering_rule}
                  {isAdmin && p.owner_doctor_member_id !== viewer?.memberId
                    ? ` · by ${p.owner_doctor_member_id}` : ''}
                </option>
              ))}
            </select>
            {!isLoadingPlans && visiblePlans.length === 0 && (
              <span className="text-xs text-slate-600 dark:text-slate-300">
                No plans yet. Author one at <em>Treatment Plans</em>.
              </span>
            )}
          </label>

          {/* Phase 2.8 — plan-level payment-mode radio. Choice is recorded
              on the PatientPlan; payment collection happens later on
              PatientPlanPage / per-visit. */}
          {selectedPlan && (
            <div className="rounded-md border border-indigo-300 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/30 p-3">
              <div className="text-xs font-bold text-indigo-900 dark:text-indigo-100 mb-2">
                Payment plan
              </div>
              <div className="flex flex-wrap gap-3">
                <label className="flex items-center gap-2 text-sm font-medium text-slate-800 dark:text-slate-100">
                  <input
                    type="radio"
                    name="attach-payment-mode"
                    checked={paymentMode === 'per_step'}
                    onChange={() => setPaymentMode('per_step')}
                    disabled={busy}
                    className="h-4 w-4"
                  />
                  Pay per visit
                </label>
                <label className="flex items-center gap-2 text-sm font-medium text-slate-800 dark:text-slate-100">
                  <input
                    type="radio"
                    name="attach-payment-mode"
                    checked={paymentMode === 'upfront'}
                    onChange={() => setPaymentMode('upfront')}
                    disabled={busy}
                    className="h-4 w-4"
                  />
                  Pay full plan upfront
                  <span className="text-xs font-semibold text-indigo-700 dark:text-indigo-200">
                    (₹{totalEstimate.toLocaleString('en-IN')} total)
                  </span>
                </label>
              </div>
              <p className="text-[11px] mt-2 text-slate-600 dark:text-slate-300">
                {paymentMode === 'upfront'
                  ? 'Receptionist collects the full estimate from PatientPlanPage before any step starts.'
                  : 'Per-visit collection — each step charges when started.'}
              </p>
            </div>
          )}

          {/* Steps */}
          {selectedPlan && !teamsLoaded && (
            <div className="text-sm text-slate-600 dark:text-slate-300 italic py-3 text-center border border-dashed border-slate-300 dark:border-slate-600 rounded-md">
              Loading team and staff…
            </div>
          )}
          {selectedPlan && teamsLoaded && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-xs font-bold text-indigo-900 dark:text-indigo-100">
                  Steps ({(selectedPlan.steps || []).length})
                </div>
                {missingStepIds.length > 0 && (
                  <button
                    type="button"
                    onClick={defaultAllToMe}
                    className="text-xs font-semibold px-2.5 py-1 rounded-md border border-indigo-500 bg-white dark:bg-slate-800 text-indigo-700 dark:text-indigo-200 hover:bg-indigo-50 dark:hover:bg-indigo-900/40"
                  >
                    Default all to me
                  </button>
                )}
              </div>

              {(selectedPlan.steps || []).map((s) => {
                const d = stepDraft[s.step_id] || { included: true };
                const isMissing = missingStepIds.includes(s.step_id);
                const baseBorder = isMissing
                  ? 'border-red-500 dark:border-red-400 ring-1 ring-red-300'
                  : 'border-indigo-300 dark:border-indigo-700';
                return (
                  <div
                    key={s.step_id}
                    className={
                      'p-3 rounded-md border-2 bg-indigo-50 dark:bg-indigo-900/30 space-y-2 '
                      + baseBorder
                    }
                  >
                    <div className="flex items-center justify-between gap-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={!!d.included}
                          disabled={busy || s.is_mandatory}
                          onChange={(e) => setStep(s.step_id, { included: e.target.checked })}
                          className="h-4 w-4"
                        />
                        <span className="text-sm font-bold text-indigo-900 dark:text-indigo-100">
                          {s.step_number}. {procedureNameById[s.procedure_id] || s.procedure_id}
                        </span>
                        {s.is_mandatory ? (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-indigo-200 dark:bg-indigo-800 text-indigo-900 dark:text-indigo-100">
                            Mandatory
                          </span>
                        ) : (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-200 dark:bg-amber-800 text-amber-900 dark:text-amber-100">
                            Optional
                          </span>
                        )}
                      </label>
                    </div>

                    {/* Phase 2.8 — three-way date radio per step. */}
                    <div className="flex flex-wrap items-center gap-3 text-xs font-medium text-slate-800 dark:text-slate-100">
                      <span className="font-semibold text-slate-700 dark:text-slate-200">
                        When:
                      </span>
                      <label className="flex items-center gap-1.5">
                        <input
                          type="radio"
                          name={`date-mode-${s.step_id}`}
                          checked={d.date_mode === DATE_MODE_TODAY}
                          onChange={() => setStep(s.step_id, { date_mode: DATE_MODE_TODAY })}
                          disabled={busy || !d.included}
                          className="h-3.5 w-3.5"
                        />
                        Today
                      </label>
                      <label className="flex items-center gap-1.5">
                        <input
                          type="radio"
                          name={`date-mode-${s.step_id}`}
                          checked={d.date_mode === DATE_MODE_PICK}
                          onChange={() => setStep(s.step_id, { date_mode: DATE_MODE_PICK })}
                          disabled={busy || !d.included}
                          className="h-3.5 w-3.5"
                        />
                        Pick date…
                      </label>
                      {d.date_mode === DATE_MODE_PICK && (
                        <input
                          type="date"
                          value={d.picked_date || ''}
                          onChange={(e) => setStep(s.step_id, { picked_date: e.target.value })}
                          disabled={busy || !d.included}
                          className="text-xs px-2 py-1 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                        />
                      )}
                      <label className="flex items-center gap-1.5">
                        <input
                          type="radio"
                          name={`date-mode-${s.step_id}`}
                          checked={d.date_mode === DATE_MODE_PLAN_ONLY}
                          onChange={() => setStep(s.step_id, { date_mode: DATE_MODE_PLAN_ONLY })}
                          disabled={busy || !d.included}
                          className="h-3.5 w-3.5"
                        />
                        Plan-only (no date yet)
                      </label>
                    </div>

                    {/* Phase 2.8 — assignees always visible (decoupled
                        from `included` so admins / receptionists can
                        pre-stage routing even on excluded optionals). */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <label className="block">
                        <span className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
                          Executor
                        </span>
                        <TeamAssignmentPicker
                          doctorMemberId={selectedPlan.owner_doctor_member_id}
                          doctorName={ownerDoctorDisplayName}
                          value={d.executor_member_id}
                          onChange={(v) => setStep(s.step_id, { executor_member_id: v })}
                          staff={staff}
                          highlight={isMissing}
                          disabled={busy || !d.included}
                          placeholder="Pick an assignee…"
                          visitDepartmentId={visitDepartmentId}
                          deptsByMember={deptsByMember}
                        />
                      </label>
                      {s.requires_approval && (
                        <label className="block">
                          <span className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
                            Approver
                          </span>
                          <TeamAssignmentPicker
                            doctorMemberId={selectedPlan.owner_doctor_member_id}
                            doctorName={ownerDoctorDisplayName}
                            value={d.approver_member_id}
                            onChange={(v) => setStep(s.step_id, { approver_member_id: v })}
                            staff={staff}
                            roleFilter="DOCTOR"
                            disabled={busy || !d.included}
                            placeholder="Approver…"
                            visitDepartmentId={visitDepartmentId}
                            deptsByMember={deptsByMember}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {errorMsg && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
              {errorMsg}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t-2 border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/30">
          {/* Phase 2.8 — running estimate. Reflects override-cost-aware
              priority + package_discount. */}
          <div className="text-xs font-semibold text-indigo-900 dark:text-indigo-100">
            {selectedPlan ? (
              <>Estimate: ₹{totalEstimate.toLocaleString('en-IN')}</>
            ) : (
              <>&nbsp;</>
            )}
          </div>
          <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-sm font-medium px-3 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={busy || !selectedPlan}
            className="text-sm font-semibold px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-60"
          >
            {busy ? 'Attaching…' : 'Attach plan to visit'}
          </button>
          </div>
        </div>
      </div>
    </div>
  );
}
